import { NavLink } from "react-router-dom";
import { useMemo } from "react";
import { Icon, type IconName } from "@/components/icons/Icon";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/lib/auth";
import { useTicketCounters } from "@/features/tickets/api";

/**
 * rc136 — Barra de pestañas inferior (solo <md), estilo ByteOps.
 *
 * Cuatro destinos fijos según permisos + "Más" que abre el menú completo
 * (el drawer lateral de siempre). El orden de PRIORIDAD decide cuáles cuatro
 * ve cada rol: un técnico sin pagos ve Tickets/Clientes/Prospectos/Mapa; un
 * cobrador ve Pagos/Cobranza. Cajero: solo Inicio + Más (su flujo es Pago
 * rápido en la barra superior).
 *
 * No es `fixed`: vive como último hijo de la columna del Layout, así el
 * <main> nunca queda debajo de ella y no hay que calcular paddings.
 */
type Tab = { to: string; label: string; iconName: IconName; perm: string | null; end?: boolean; badge?: "tickets" };

const PRIORIDAD: Tab[] = [
  { to: "/", label: "Inicio", iconName: "dashboard", perm: "dashboard.read", end: true },
  { to: "/tickets", label: "Tickets", iconName: "ticket", perm: "tickets.read", badge: "tickets" },
  { to: "/clientes", label: "Clientes", iconName: "users", perm: "clientes.read" },
  { to: "/pagos", label: "Pagos", iconName: "wallet", perm: "pagos.read" },
  { to: "/prospectos", label: "Prospectos", iconName: "user", perm: "prospectos.read" },
  { to: "/cobranza", label: "Cobranza", iconName: "phone-call", perm: "cobranza.read" },
  { to: "/contratos", label: "Contratos", iconName: "file-text", perm: "contratos.read" },
  { to: "/mapa", label: "Mapa", iconName: "map-pin", perm: "contratos.read" },
  { to: "/facturas", label: "Facturas", iconName: "receipt", perm: "facturas.read" },
];
const MAX_TABS = 4;

function TicketsDot() {
  const { data } = useTicketCounters();
  const total = data ? data.abiertos_total + data.en_proceso_total : 0;
  if (!total) return null;
  return (
    <span className="absolute -right-2.5 -top-1 min-w-[16px] rounded-full bg-amber-500 px-1 text-center text-[9px] font-semibold leading-[16px] text-black">
      {total > 99 ? "99+" : total}
    </span>
  );
}

export function MobileTabBar({ onMore, menuOpen }: { onMore: () => void; menuOpen: boolean }) {
  const permissions = useAuthStore((s) => s.permissions);
  const user = useAuthStore((s) => s.user);
  const isCajero = user?.rol?.nombre === "cajero";

  const tabs = useMemo(() => {
    if (isCajero) return PRIORIDAD.filter((t) => t.to === "/");
    return PRIORIDAD.filter((t) => !t.perm || permissions.includes(t.perm)).slice(0, MAX_TABS);
  }, [permissions, isCajero]);

  const cols = tabs.length + 1;

  return (
    <nav
      aria-label="Navegación principal"
      className="grid shrink-0 border-t border-border bg-background pb-[env(safe-area-inset-bottom)] md:hidden"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
    >
      {tabs.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) =>
            cn(
              "tap-compact flex h-[54px] flex-col items-center justify-center gap-1 text-[10.5px] leading-none transition-colors",
              isActive && !menuOpen ? "font-semibold text-electric" : "text-muted-foreground",
            )
          }
        >
          <span className="relative">
            <Icon name={t.iconName} size={21} />
            {t.badge === "tickets" && <TicketsDot />}
          </span>
          {t.label}
        </NavLink>
      ))}
      <button
        type="button"
        onClick={onMore}
        className={cn(
          "tap-compact flex h-[54px] flex-col items-center justify-center gap-1 text-[10.5px] leading-none transition-colors",
          menuOpen ? "font-semibold text-electric" : "text-muted-foreground",
        )}
        aria-label="Abrir menú completo"
        aria-expanded={menuOpen}
      >
        <Icon name="menu" size={21} />
        Más
      </button>
    </nav>
  );
}
